import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import { PATH_DASHBOARD } from './paths';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
      ],
    },

    // Dashboard Routes
    {
      path: '/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_DASHBOARD.statistic.root} replace />, index: true },

        { path: 'statistic', element: <Statistic /> },

        { path: 'settings/general', element: <Setting /> },
        { path: 'topup', element: <TopupAegpay /> },

        { path: 'user', element: <UserList /> },
        { path: 'user/new', element: <UserCreateUpdate /> },
        { path: 'user/:id/edit', element: <UserCreateUpdate /> },

        { path: 'order', element: <OrderList /> },
        { path: 'order/:id', element: <OrderView /> },

        { path: 'payment', element: <PaymentList /> },
        { path: 'payment/new-link', element: <PaymentCreateLink /> },

        { path: 'settings/delivery', element: <DeliveryList /> },

        { path: 'settings/exchange', element: <ExchangeList /> },

        { path: 'settings/gateway', element: <GatewayList /> },

        { path: 'settings/terminal', element: <TerminalList /> },

        { path: 'request', element: <UserRequestList /> },
        { path: 'request/:id', element: <UserRequestView /> },

        { path: 'withdrawal', element: <WithdrawalList /> },
        { path: 'withdrawal/:id', element: <WithdrawalView /> },

        { path: 'wallet-history', element: <WalletHistory /> },

        { path: 'wallet-list', element: <Wallet /> },

        { path: 'bank-card/production', element: <BankCardProductionList /> },
        { path: 'bank-card/list', element: <BankCardList /> },
        { path: 'bank-card/new', element: <BankCardCreate /> },
        { path: 'bank-card/:id/view', element: <BankCardView /> },
        { path: 'bank-card/add-pending', element: <BankCardAddPending /> },

        { path: 'discount-coupon/list', element: <DiscountCouponList /> },
        { path: 'discount-coupon/new', element: <DiscountCouponCreateUpdate /> },
        { path: 'discount-coupon/:id/edit', element: <DiscountCouponCreateUpdate /> },

        { path: 'ticket/list', element: <TicketList /> },
        { path: 'ticket/new', element: <TicketCreate /> },
        { path: 'ticket/:id/view', element: <TicketView /> },

        { path: 'redeem-code/list', element: <RedeemCodeTable /> },

        { path: 'partner', element: <PartnerList /> },
        { path: 'partner/new', element: <PartnerCreateUpdate /> },
        { path: 'partner/:id/edit', element: <PartnerCreateUpdate /> },
        { path: 'card-requests', element: <CardRequests /> },
        { path: 'kyc-requests', element: <ChargeRequests /> },
        { path: 'kyc-requests/:id', element: <UserKycForm /> },
        { path: 'address-requests', element: <UserAddressRequests /> },

        { path: 'address-requests/:id', element: <UserAddressForm /> },
        { path: 'settings/plans', element: <Plans /> },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '/', element: <Navigate to="/dashboard" replace /> },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));

// Dashboard
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

// Statistic
const Statistic = Loadable(lazy(() => import('../pages/statistic/Statistic')));

// User
const UserList = Loadable(lazy(() => import('../pages/user/UserList')));
const UserCreateUpdate = Loadable(lazy(() => import('../pages/user/UserCreateUpdate')));

// Order
const OrderList = Loadable(lazy(() => import('../pages/order/OrderList')));
const OrderView = Loadable(lazy(() => import('../pages/order/OrderView')));

// Payment
const PaymentList = Loadable(lazy(() => import('../pages/payment/PaymentList')));
const PaymentCreateLink = Loadable(lazy(() => import('../pages/payment/PaymentCreateLink')));

// Delivery
const DeliveryList = Loadable(lazy(() => import('../pages/delivery/DeliveryList')));

// Exchange
const ExchangeList = Loadable(lazy(() => import('../pages/exchange/ExchangeList')));

// Terminal
const TerminalList = Loadable(lazy(() => import('../pages/terminal/TerminalList')));

// Gateway
const GatewayList = Loadable(lazy(() => import('../pages/gateway/GatewayList')));

// UserRequest
const UserRequestList = Loadable(lazy(() => import('../pages/userRequest/UserRequestList')));
const UserRequestView = Loadable(lazy(() => import('../pages/userRequest/UserRequestView')));

// Withdrawal
const WithdrawalList = Loadable(lazy(() => import('../pages/withdrawal/WithdrawalList')));
const WithdrawalView = Loadable(lazy(() => import('../pages/withdrawal/WithdrawalView')));

// WalletHistory
const WalletHistory = Loadable(lazy(() => import('../pages/walletHistory/WalletHistoryList')));

// Wallet
const Wallet = Loadable(lazy(() => import('../pages/wallet/WalletList')));

// Setting
const Setting = Loadable(lazy(() => import('../pages/setting/Setting')));

// Bank Card
const BankCardProductionList = Loadable(lazy(() => import('../pages/bankCardProduction/BankCardProductionList')));
const BankCardList = Loadable(lazy(() => import('../pages/bankCard/BankCardList')));
const BankCardCreate = Loadable(lazy(() => import('../pages/bankCard/BankCardCreate')));
const BankCardView = Loadable(lazy(() => import('../pages/bankCard/bankCardView/BankCardView')));
const BankCardAddPending = Loadable(lazy(() => import('../pages/bankCard/BankCardAddPending')));

// DiscountCoupon
const DiscountCouponList = Loadable(lazy(() => import('../pages/discountCoupon/DiscountCouponList')));
const DiscountCouponCreateUpdate = Loadable(lazy(() => import('../pages/discountCoupon/DiscountCouponCreateUpdate')));

// Ticket
const TicketList = Loadable(lazy(() => import('../pages/ticket/TicketList')));
const TicketCreate = Loadable(lazy(() => import('../pages/ticket/TicketCreate')));
const TicketView = Loadable(lazy(() => import('../pages/ticket/TicketView')));

// Redeem Code
const RedeemCodeTable = Loadable(lazy(() => import('../pages/redeemCode/RedeemCodeTable')));

// Partner
const PartnerList = Loadable(lazy(() => import('../pages/partner/PartnerList')));
const PartnerCreateUpdate = Loadable(lazy(() => import('../pages/partner/PartnerCreateUpdate')));

// Card Requests

const CardRequests = Loadable(lazy(() => import('../pages/cardrequests/CardRequests')));


// Charge Requests

const ChargeRequests = Loadable(lazy(() => import('../pages/charge-requests/ChargeRequests')));

// address requests
const UserAddressRequests = Loadable(lazy(() => import('../pages/address-requests/ChargeRequests')));
const UserAddressForm = Loadable(lazy(() => import('../pages/address-requests/UserKycForm')));

// Plans

const Plans = Loadable(lazy(() => import('../pages/plans/Plans')));
const UserKycForm = Loadable(lazy(() => import('../pages/charge-requests/UserKycForm')));

// topup for aegpay account

const TopupAegpay = Loadable(lazy(() => import('../pages/topup-aegpay/TopupAegpay')))
